import { Appointment } from '@/mocks/appointments';

interface Props {
  weekStart: Date;
  appointments: Appointment[];
  onDayClick: (dateStr: string) => void;
  onAptClick: (apt: Appointment) => void;
  onAptDelete: (id: string) => void;
}

const gunAdlari = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const gunAdlariFull = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

const renkMap: Record<string, { bg: string; text: string; border: string; dot: string }> = {
  sky:    { bg: 'bg-sky-50',    text: 'text-sky-800',    border: 'border-l-sky-500',    dot: 'bg-sky-500' },
  emerald:{ bg: 'bg-emerald-50',text: 'text-emerald-800',border: 'border-l-emerald-500',dot: 'bg-emerald-500' },
  orange: { bg: 'bg-orange-50', text: 'text-orange-800', border: 'border-l-orange-400', dot: 'bg-orange-400' },
  red:    { bg: 'bg-red-50',    text: 'text-red-800',    border: 'border-l-red-500',    dot: 'bg-red-500' },
  violet: { bg: 'bg-violet-50', text: 'text-violet-800', border: 'border-l-violet-500', dot: 'bg-violet-500' },
};

export default function HaftalikTakvim({ weekStart, appointments, onDayClick, onAptClick, onAptDelete }: Props) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });

  const getDateStr = (d: Date) => d.toISOString().split('T')[0];

  const aptsByDay = (dateStr: string) =>
    appointments
      .filter(a => a.tarih === dateStr)
      .sort((a, b) => a.saat.localeCompare(b.saat));

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      {/* Header row */}
      <div className="grid grid-cols-7 border-b border-gray-100">
        {days.map((d, i) => {
          const isToday = d.getTime() === today.getTime();
          const dateStr = getDateStr(d);
          const count = aptsByDay(dateStr).length;
          return (
            <div
              key={i}
              className="border-r border-gray-100 last:border-r-0 text-center py-3 px-1"
            >
              <p className="text-xs text-gray-400 font-medium hidden sm:block">{gunAdlariFull[i]}</p>
              <p className="text-xs text-gray-400 font-medium sm:hidden">{gunAdlari[i]}</p>
              <div className={`w-8 h-8 flex items-center justify-center rounded-full mx-auto mt-1 font-bold text-sm
                ${isToday ? 'bg-sky-600 text-white' : 'text-gray-700'}`}>
                {d.getDate()}
              </div>
              <p className="text-xs text-gray-400 mt-0.5">
                {d.toLocaleDateString('tr-TR', { month: 'short' })}
              </p>
              {count > 0 && (
                <span className="inline-block mt-1 text-xs bg-sky-100 text-sky-700 font-bold px-1.5 py-0.5 rounded-full">
                  {count}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Body rows */}
      <div className="grid grid-cols-7 min-h-[400px]">
        {days.map((d, i) => {
          const isToday = d.getTime() === today.getTime();
          const dateStr = getDateStr(d);
          const dayApts = aptsByDay(dateStr);

          return (
            <div
              key={i}
              className={`border-r border-gray-100 last:border-r-0 p-1.5 min-h-[400px] cursor-pointer transition-colors
                ${isToday ? 'bg-sky-50/40' : 'hover:bg-slate-50/60'}`}
              onClick={() => onDayClick(dateStr)}
            >
              {dayApts.length === 0 && (
                <div className="h-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-add-circle-line text-gray-300 text-xl"></i>
                  </div>
                </div>
              )}
              <div className="space-y-1.5" onClick={e => e.stopPropagation()}>
                {dayApts.map(apt => {
                  const stil = renkMap[apt.renk] || renkMap.sky;
                  return (
                    <div
                      key={apt.id}
                      className={`rounded-lg border-l-4 p-2 ${stil.bg} ${stil.border} cursor-pointer hover:brightness-95 transition-all group relative`}
                      onClick={() => onAptClick(apt)}
                    >
                      <div className="flex items-start justify-between gap-1">
                        <div className="min-w-0 flex-1">
                          <p className={`text-xs font-bold truncate ${stil.text}`}>{apt.musteriAdi}</p>
                          <p className="text-xs text-gray-500 mt-0.5 hidden sm:block truncate">{apt.islemTuru}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <span className="text-xs font-semibold text-gray-600 bg-white/70 px-1 rounded">{apt.saat}</span>
                          <button
                            onClick={e => { e.stopPropagation(); onAptDelete(apt.id); }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity w-4 h-4 flex items-center justify-center text-red-400 hover:text-red-600 cursor-pointer"
                          >
                            <i className="ri-close-line text-xs"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
